import React, { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { CopyProps } from 'Src/components/Copy';
import FontIcon from 'Src/components/icons/FontIcon';
import { useCheckItemBuilder } from 'Src/feature/check-item/useCheckItemBuilder';
import { ModifierChoice, ModifierTreeItem } from 'Src/feature/modifier/useModifierTree';
import { useResizeObserver } from 'Src/feature/resize/useResizeObserver';
import { ModelType } from 'Src/models/modelPath';

import { ModifierBreadCrumbButton } from './ModifierBreadCrumbButton';

import styles from './ModifierBreadCrumbs.module.scss';

const sectionId = 'ModifierBreadCrumbs';

interface ModifierBreadCrumbsProps {
  modifierPath: ModifierTreeItem['path']['full'];
  activeId?: string;
  updateActiveId: (id: string) => void;
}

const ModifierBreadCrumbs: FC<ModifierBreadCrumbsProps> = ({
  modifierPath,
  activeId,
  updateActiveId,
}) => {
  const { t } = useTranslation();
  const checkItemBuilder = useCheckItemBuilder();
  const modifiers = checkItemBuilder.modifiers;

  const rootRef = useRef(null as null | HTMLDivElement);

  const contentResize = useResizeObserver({
    onResize: () => {
      // scroll to the last element every time the container resizes
      // - resize will happen when a new child is added
      rootRef.current?.scrollTo({
        left: rootRef.current?.scrollWidth,
        behavior: 'smooth'
      });
    }
  });

  // last most modifier
  let activeModifier = undefined as undefined | ModifierTreeItem;
  modifiers.tree.root?.walk('down', (item) => {
    if (item.path.full === activeId) {
      // deterministic from modifiers.tree.root to avoid stale state
      activeModifier = item;
      throw 'break';
    }
  });

  const activeBreadCrumbModifiers: ModifierTreeItem[] = [];
  if (activeModifier) {
    activeModifier.walk('up', (item) => {
      if (!item.path.full.includes(modifierPath)) return;
      activeBreadCrumbModifiers.unshift(item);
    });
  }

  const onBreadCrumbClick = (event: React.MouseEvent<HTMLButtonElement>, modifier: ModifierTreeItem) => {
    // const modifierIdFromElement = ;
    // rebuild modifier breadcrumbs when selecting a ModifierGroup
    // - walk up from "next" selection to append more bread crumbs
    // - omitting the main button modifier & the menu item root node
    if (modifier.type === ModelType.modifierGroup) {
      updateActiveId(modifier.path.full);
    }

    // scroll to the right most
    rootRef.current?.scrollTo({
      left: event.currentTarget.offsetLeft,
      behavior: 'smooth'
    });
  };

  return (
    <div
      ref={rootRef}
      className={styles.root}
    >
      <div ref={contentResize.setElement} className={styles.scrollContent}>
        {
          activeBreadCrumbModifiers.map((modifier, i) => {
            const name = modifier.name;
            const isFirst = i === 0;
            const isActive = modifier.path.full === activeId;
            let message: string | undefined = undefined;
            let messageColor: CopyProps['color'] = 'textSecondary';
            // only show messages for the active modifier bread crumb button
            if (isActive) {
              // validation/choices only available for ModifierGroup
              if (modifier.choices && modifier.validation) {
                message = t('modifier.choice.message.wrapped', { message: (modifier.validation.messages || []).join(',') });
                // const error = (modifier.validation.errors || []).join(',');
                if (modifier.choices.type === ModifierChoice.Any) {
                  messageColor = 'textSecondary';
                } else {
                  messageColor = modifier.valid === false ? 'error' : 'textPrimary';
                }
              }
            }

            return (
              <React.Fragment key={modifier.path.full}>
                { i !== 0 && <FontIcon className={styles.divider} size="md" name="navigate_next" />}
                <ModifierBreadCrumbButton
                  data-option-id={modifier.path.full}
                  data-testid={`bread-crumb-${modifier.path.full}`}
                  isFirst={isFirst}
                  isActive={isActive}
                  name={name}
                  message={message}
                  messageColor={messageColor}
                  onClick={(event) => onBreadCrumbClick(event, modifier)}
                />
              </React.Fragment>
            );
          })
        }
      </div>
    </div>
  );
};

export { ModifierBreadCrumbs, sectionId };
export type { ModifierBreadCrumbsProps };

